/**
 * Associações (admin) — persistência em localStorage e sincronização com DynamoDB (tabela dedicada).
 */

import type { StatusCadastro } from './guiasAgenciasStorage';

export const KEY_ASSOCIACOES_STORAGE = 'naviex-admin-associacoes';

const LEGACY_MIGRATION_KEY = 'naviex-migrated-associacoes-from-agencias-v1';

export interface Associacao {
  id: string;
  razaoSocial: string;
  email: string;
  telefone: string;
  cnpj: string;
  cidadeId: string;
  enderecoLogradouro: string;
  enderecoNumero: string;
  enderecoComplemento?: string;
  enderecoBairro?: string;
  enderecoCompleto: string;
  status: StatusCadastro;
  guiaIds: string[];
  createdAt: string;
  updatedAt: string;
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const s = localStorage.getItem(key);
    return s ? (JSON.parse(s) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, data: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/** Itens antigos gravados em agências com cidadeId passam para esta chave (uma vez por browser). */
function migrateLegacyAssociacoesFromAgenciasOnce(): void {
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(LEGACY_MIGRATION_KEY)) return;
  try {
    const agRaw = localStorage.getItem('naviex-admin-agencias');
    if (!agRaw) {
      localStorage.setItem(LEGACY_MIGRATION_KEY, '1');
      return;
    }
    const list = JSON.parse(agRaw) as unknown[];
    if (!Array.isArray(list)) {
      localStorage.setItem(LEGACY_MIGRATION_KEY, '1');
      return;
    }
    const toAssoc: Associacao[] = [];
    const remaining: unknown[] = [];
    for (const row of list) {
      if (
        row &&
        typeof row === 'object' &&
        'cidadeId' in row &&
        String((row as { cidadeId?: unknown }).cidadeId ?? '').trim()
      ) {
        const r = row as Record<string, unknown>;
        const id = String(r.id ?? '');
        if (!id) continue;
        toAssoc.push({
          id,
          razaoSocial: String(r.razaoSocial ?? ''),
          email: String(r.email ?? ''),
          telefone: String(r.telefone ?? ''),
          cnpj: String(r.cnpj ?? ''),
          cidadeId: String(r.cidadeId ?? ''),
          enderecoLogradouro: String(r.enderecoLogradouro ?? ''),
          enderecoNumero: String(r.enderecoNumero ?? ''),
          enderecoComplemento: r.enderecoComplemento ? String(r.enderecoComplemento) : undefined,
          enderecoBairro: r.enderecoBairro ? String(r.enderecoBairro) : undefined,
          enderecoCompleto: String(r.enderecoCompleto ?? ''),
          status: (r.status as StatusCadastro) ?? 'pendente',
          guiaIds: Array.isArray(r.guiaIds) ? r.guiaIds.map(String) : [],
          createdAt: String(r.createdAt ?? new Date().toISOString()),
          updatedAt: String(r.updatedAt ?? new Date().toISOString()),
        });
      } else {
        remaining.push(row);
      }
    }
    if (toAssoc.length > 0) {
      const existing = loadJson<Associacao[]>(KEY_ASSOCIACOES_STORAGE, []);
      const merged = [...existing];
      for (const a of toAssoc) {
        if (!merged.some((x) => x.id === a.id)) merged.push(a);
      }
      saveJson(KEY_ASSOCIACOES_STORAGE, merged);
      localStorage.setItem('naviex-admin-agencias', JSON.stringify(remaining));
    }
    localStorage.setItem(LEGACY_MIGRATION_KEY, '1');
  } catch {
    localStorage.setItem(LEGACY_MIGRATION_KEY, '1');
  }
}

export function getAssociacoes(): Associacao[] {
  migrateLegacyAssociacoesFromAgenciasOnce();
  return loadJson<Associacao[]>(KEY_ASSOCIACOES_STORAGE, []);
}

export function getAssociacaoById(id: string): Associacao | null {
  return getAssociacoes().find((a) => a.id === id) ?? null;
}

export function saveAssociacao(associacao: Associacao): void {
  const list = getAssociacoes();
  const i = list.findIndex((a) => a.id === associacao.id);
  const now = new Date().toISOString();
  const toSave = { ...associacao, updatedAt: now };
  if (i >= 0) list[i] = toSave;
  else list.push({ ...toSave, createdAt: now });
  saveJson(KEY_ASSOCIACOES_STORAGE, list);
}

export function addAssociacao(data: Omit<Associacao, 'id' | 'createdAt' | 'updatedAt'>): Associacao {
  const now = new Date().toISOString();
  const id = `assoc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const associacao: Associacao = {
    ...data,
    guiaIds: data.guiaIds ?? [],
    id,
    createdAt: now,
    updatedAt: now,
  };
  const list = getAssociacoes();
  list.push(associacao);
  saveJson(KEY_ASSOCIACOES_STORAGE, list);
  return associacao;
}

export function setAssociacaoStatus(id: string, status: StatusCadastro): void {
  const a = getAssociacaoById(id);
  if (!a) return;
  saveAssociacao({ ...a, status });
}

const KEY_GUIAS = 'naviex-admin-guias';

export function removeAssociacao(id: string): void {
  if (!getAssociacaoById(id)) return;
  const guias = loadJson<Array<{ id: string; agenciaId?: string } & Record<string, unknown>>>(
    KEY_GUIAS,
    []
  );
  let changed = false;
  const nextGuias = guias.map((g) => {
    if (g.agenciaId === id) {
      changed = true;
      return { ...g, agenciaId: undefined };
    }
    return g;
  });
  if (changed) saveJson(KEY_GUIAS, nextGuias);
  saveJson(
    KEY_ASSOCIACOES_STORAGE,
    getAssociacoes().filter((x) => x.id !== id)
  );
}
